import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

import { FileTypeIcon } from "../FileTypeIcon";
import {
  CitationPdfViewer,
  type CitationPageOverlayRenderArgs
} from "./CitationPdfViewer";
import {
  citationHighlightRect,
  citationPageOffsetRatio,
  renderedPageHeightFor,
  type CitationGeometry
} from "./citationHighlight";

type Props = {
  /** Blob or http URL of the document the citation was measured against. */
  file: string;
  /**
   * Where the value sits. Absent when opening a document rather than one value
   * inside it, in which case nothing is highlighted.
   */
  geometry?: CitationGeometry | null;
  /** 1-based, as Extend reports it. */
  page?: number | null;
  filename?: string | null;
  /** False while closing, so the panel can animate out before it unmounts. */
  isOpen: boolean;
  onClose: () => void;
};

/**
 * A cited source document, highlighted, shown in a centered modal overlay.
 *
 * The context-agnostic sibling of the intake assistant's `CitationViewerPanel`:
 * it shares the same highlight geometry and `CitationPdfViewer`, but is not docked to a
 * particular surface, so it suits places that have no chat rail to sit beside --
 * such as the form provenance sidebar, where the form itself already fills the
 * center and the source document needs a surface of its own.
 *
 * Portalled to the body so the surrounding panel's overflow and transforms do
 * not clip or re-anchor it.
 */
export function CitationDocumentOverlay({
  file,
  geometry,
  page,
  filename,
  isOpen,
  onClose
}: Props) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Escape in the viewer's zoom menu or search popover closes just that.
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("[data-radix-popper-content-wrapper]")) return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  const renderOverlay = useCallback(
    ({ pageNumber, renderedPageWidth }: CitationPageOverlayRenderArgs) => {
      if (!geometry || pageNumber !== page) return null;

      const renderedPageHeight = renderedPageHeightFor(
        geometry,
        renderedPageWidth
      );
      if (renderedPageHeight === null) return null;

      const rect = citationHighlightRect(geometry, {
        renderedPageWidth,
        renderedPageHeight
      });
      if (rect === null) return null;

      return (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute rounded-xs bg-warn/30 ring-1 ring-warn/70"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          }}
        />
      );
    },
    [geometry, page]
  );

  if (typeof document === "undefined") return null;

  const shown = entered && isOpen;

  return createPortal(
    <div
      className={[
        "fixed inset-0 z-1600 flex items-center justify-center bg-black/40 p-4 sm:p-6",
        "transition-opacity duration-200 ease-out",
        shown ? "opacity-100" : "opacity-0"
      ].join(" ")}
    >
      <button
        type="button"
        aria-label="Close document"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-label={filename || "Document"}
        className="relative flex min-h-0 w-full max-w-225 flex-1 flex-col overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-3 sm:h-[min(88vh,900px)]"
      >
        <header className="flex h-11 shrink-0 items-center justify-between gap-3 border-b border-line-2 pr-2 pl-4">
          <span className="flex min-w-0 items-center gap-2 text-base font-medium text-fg">
            <FileTypeIcon name={filename || ""} size={15} />
            <span className="truncate">{filename || "Source Document"}</span>
            {typeof page === "number" ? (
              <span className="shrink-0 text-sm font-normal text-fg-4">
                page {page}
              </span>
            ) : null}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            onClick={onClose}
          >
            <X />
          </Button>
        </header>
        <div className="min-h-0 flex-1">
          <CitationPdfViewer
            file={file}
            ariaLabel={`${filename || "Document"} preview`}
            scrollToPageIndex={page ? Math.max(0, page - 1) : 0}
            scrollToPageOffsetRatio={
              geometry ? citationPageOffsetRatio(geometry) : null
            }
            renderPageOverlay={renderOverlay}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
